import { PageHeader, usePageHeader } from '@easydoctor/ui';
import {
  STATISTICS_DISTRIBUTIONS,
  STATISTICS_PERIODS,
  STATISTICS_PERIOD_LABELS,
  isStatisticsPeriod,
} from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useSearchParams } from 'react-router';
import { useStatistics } from '../../api/statistics';
import { HelpButton } from '../../guida/HelpButton';
import { DistributionCard } from './DistributionCard';
import { StatisticsIndicators } from './StatisticsIndicators';
import { formatDay, readPeriod, writePeriod } from './format';

/**
 * Statistiche del servizio (PRD-005 US-2, story #11, PAGE-009): volumi, tempi e distribuzione dei
 * ticket ricevuti nel periodo scelto (7, 30 o 90 giorni, nell'URL). Solo dati reali: un indicatore
 * senza dati lo dichiara, nessun valore dimostrativo.
 */
export function StatistichePage() {
  usePageHeader({ title: 'Statistiche' });
  const [search, setSearch] = useSearchParams();
  const period = readPeriod(search);
  const { data, isPending, isError, error, refetch } = useStatistics(period);

  return (
    <Stack spacing={4}>
      <PageHeader
        // Il periodo si allinea al fondo della testata, come prima di `PageHeader` (lì in alto).
        sx={{ alignItems: { sm: 'flex-end' } }}
        title="Statistiche"
        titleAdornment={<HelpButton screen="/statistiche" />}
        description={
          data
            ? `Ticket ricevuti dal ${formatDay(data.period.from)} a oggi (ultimi ${data.period.days} giorni, ora di Roma)`
            : `Ticket ricevuti negli ultimi ${period} giorni`
        }
        actions={
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            aria-label="Periodo"
            value={period}
            onChange={(_event, value: unknown) => {
              if (isStatisticsPeriod(value)) setSearch(writePeriod(value));
            }}
          >
            {STATISTICS_PERIODS.map((days) => (
              <ToggleButton key={days} value={days}>
                {STATISTICS_PERIOD_LABELS[days]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        }
      />

      {isPending && (
        <Stack direction="row" spacing={2} alignItems="center" role="status">
          <CircularProgress size={20} />
          <Typography>Calcolo delle statistiche…</Typography>
        </Stack>
      )}

      {isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              Riprova
            </Button>
          }
        >
          <AlertTitle>Statistiche non disponibili</AlertTitle>
          {error.message}
        </Alert>
      )}

      {data && (
        <>
          {data.received === 0 && (
            <Alert severity="info">
              Nessun ticket ricevuto nel periodo: gli indicatori e le distribuzioni non hanno dati da mostrare.
            </Alert>
          )}
          <StatisticsIndicators stats={data} />
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              alignItems: 'start',
            }}
          >
            {STATISTICS_DISTRIBUTIONS.map((dimension) => (
              <DistributionCard
                key={dimension}
                dimension={dimension}
                rows={data.distributions[dimension]}
                total={data.workable}
              />
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Misure, non impegni: non ci sono soglie di servizio concordate. Tempi, quote e distribuzioni riguardano i
            ticket lavorabili ricevuti nel periodo; gli scartati sono contati a parte. Nessuna distribuzione per
            prodotto.
          </Typography>
        </>
      )}
    </Stack>
  );
}
